import { FC, useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CustomPopup from "../../../components/CustomPopup/CustomPopup";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import {
  T_FaqCategoryItemList,
  T_FaqCategoryItems,
  T_FaqModuleItem,
} from "../GeneralAppElements_Types";
import { Colors } from "../../../styles/constant";
import FaqItemList from "../FaqItemList/FaqItemList";
import { useLazyFetchFaqCategoriesQuery } from "../GeneralAppElements_Api";
import CustomShimmer from "../../../components/CustomShimmer/CustomShimmer";
import {
  FAQDisclaimerContent,
  FAQDisclaimerTitle,
  tabViewPoint,
} from "../../../utils/constants";
import "./FAQsMobile.scss";
import useWindowSize from "../../../hooks/useWindowSize";
import { setCategoryList } from "../GeneralAppElements_Slice";
import { useLocation } from "react-router-dom";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import CloseIcon from "@mui/icons-material/Close";
import {
  FaqApiAbortController,
  getGTAEventScreenType,
  LobNameWithCode,
} from "../../../utils/helper";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import TriggerGTAEvent from "../../../utils/GTA";
import {
  R_HiRoutes,
  R_HLRoutes,
  R_LiRoutes,
  R_MfRoutes,
  R_SSRoutes,
} from "../../../utils/Routes";
import { isEmpty, uniq } from "lodash";

interface Props {
  open: boolean;
  handleOpen: Function;
  openChatWithUs: Function;
  activeTab: string;
  fromDetail?: boolean;
}

interface ChildProps {
  data: T_FaqCategoryItems;
  handleCategory: Function;
}

interface WorkflowInfo {
  [key: string]: any;
}

const FAQsMobile: FC<Props> = ({
  open,
  handleOpen,
  activeTab = "",
  openChatWithUs,
  fromDetail = false,
}) => {
  // const appDispatch = useAppDispatch();
  const [faqCategories, setFaqCategories] = useState<T_FaqCategoryItems[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<T_FaqCategoryItems>({
    attributes: {
      createdAt: "",
      key: "",
      categoryName: "",
      publishedAt: "",
      updatedAt: "",
    },
    id: 0,
    moduleName: "",
  });
  const [fetchFaqCategoriesApi] = useLazyFetchFaqCategoriesQuery();
  const { width } = useWindowSize();
  const dispatch = useAppDispatch();
  const selectedModule = useAppSelector(
    (state) => state.GeneralAppElementsSlice.selectedModule
  );
  const parentModuleList = useAppSelector(
    (state) => state.GeneralAppElementsSlice.parentModuleList
  );
  const [step, setStep] = useState<number>(1);
  const [fAQLobList, setFAQLobList] = useState<string[]>([""]);
  const customerAndLobDetails = useAppSelector(
    (state) => state.CsAppSlice.userDetails?.customerAndLobDetails || []
  );
  const categoryList = useAppSelector(
    (state) => state.GeneralAppElementsSlice.categoryList
  );
  const [showShimmer, setShowCategoryShimmer] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      FaqApiAbortController.AbortController?.abort();
    };
  }, []);

  useEffect(() => {
    if (parentModuleList.length > 0) {
      fetchFaqCategoryList();
    }
  }, [parentModuleList, activeTab, fromDetail]);

  useEffect(() => {
    setFaqCategories(categoryList);
  }, [categoryList]);

  const handleClosePopup = useCallback(() => {
    if (step === 1) {
      handleOpen();
    }
    if (step === 2) {
      setStep(1);
    } else {
      setSelectedCategory({
        attributes: {
          createdAt: "",
          key: "",
          categoryName: "",
          publishedAt: "",
          updatedAt: "",
        },
        id: 0,
        moduleName: "",
      });
      setStep(2);
    }
  }, [step, handleOpen]);

  const handleCategory = useCallback(
    (data: T_FaqCategoryItems, isShow: boolean) => {
      if (isShow) {
        setSelectedCategory(data);
      } else {
        setSelectedCategory({
          attributes: {
            createdAt: "",
            key: "",
            categoryName: "",
            publishedAt: "",
            updatedAt: "",
          },
          id: 0,
          moduleName: "",
        });
      }
      if (isShow === true) {
        setStep(3);
      } else {
        setStep(2);
      }
    },
    [step]
  );

  const fetchFaqCategoryList = useCallback(async () => {
    try {
      setShowCategoryShimmer(true);
      FaqApiAbortController.AbortController = new AbortController();
      let filterData = customerAndLobDetails
        ?.filter((item) => !isEmpty(item.LOB))
        .map((item) => item.LOB)
        .reduce<string[]>((acc, lob) => {
          if (!acc.includes(lob)) acc.push(lob);
          return acc;
        }, []);
      filterData.unshift("00");
      setFAQLobList(filterData);
    } catch (e) {
      console.log(e);
    } finally {
      setShowCategoryShimmer(false);
    }
  }, [fromDetail, activeTab]);

  const handleLobClick = async (lobId: string) => {
    setShowCategoryShimmer(true);
    setStep(2);
    let parentModuleDetails: any[] = [];
    [...parentModuleList].forEach((item) => {
      if (item.attributes.modules.data.length > 0) {
        parentModuleDetails.push(...item.attributes.modules.data);
      }
    });
    if (fAQLobList.length > 0) {
      let matchedObj = parentModuleDetails.filter((item) => {
        return item.attributes.lobId === lobId;
      });
      if (isEmpty(matchedObj)) return null;
      FaqApiAbortController.AbortController = new AbortController();
      const results = await fetchFaqCategoriesApi(matchedObj[0].attributes.key);
      let categories: any[] = [];
      let errors: any[] = [];
      if (!isEmpty(results) && results?.status !== "rejected") {
        if (results.data?.code === API_STATUS_CODE.SUCCESS) {
          results?.data?.payload?.data?.map((data: any) => {
            data = JSON.parse(JSON.stringify(data));
            data["moduleName"] = results.originalArgs;
            categories.push(data);
            return data;
          });
        } else {
          errors.push(results);
        }
      }
      if (errors.length > 0) {
        dispatch(
          setError({
            error: new Error("Error"),
            message: "Fetch FAQ categories failed",
          })
        );
      }
      dispatch(setCategoryList(categories));
      setShowCategoryShimmer(false);
    }
  };

  const renderFirstLevel = useCallback(() => {
    return (
      <Grid container className="faq-mbl-items" spacing={"0.564rem"}>
        {!showShimmer ? (
          fAQLobList.length > 0 ? (
            fAQLobList.map((data) => {
              return (
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={6}
                  className="faq-mbl-items-grid"
                  key={data}
                >
                  <Typography
                    className="faq-mbl-item"
                    variant="subtitle2"
                    onClick={() => {
                      handleLobClick(data);
                    }}
                  >
                    {LobNameWithCode(data)}
                  </Typography>
                </Grid>
              );
            })
          ) : (
            <Grid item md={12} sm={12} xs={12} className="faq-mbl-items-grid">
              <Typography
                variant="h5"
                sx={{ textAlign: "center", width: "100%" }}
              >
                No categories found
              </Typography>
            </Grid>
          )
        ) : (
          <>
            {[1, 2, 3, 4].map((data) => {
              return (
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={6}
                  className="faq-mbl-items-grid"
                  key={data}
                >
                  <Typography className="faq-mbl-item" variant="subtitle2">
                    <CustomShimmer width="100%" height="1.063rem" />
                  </Typography>
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    );
  }, [showShimmer, faqCategories]);

  const renderSteps = useCallback(() => {
    return (
      <Grid container className="faq-mbl-items" spacing={"0.564rem"}>
        {!showShimmer ? (
          faqCategories.length > 0 ? (
            faqCategories.map((data) => {
              return (
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={6}
                  className="faq-mbl-items-grid"
                  key={data.attributes.key}
                >
                  <RenderTitle data={data} handleCategory={handleCategory} />
                </Grid>
              );
            })
          ) : (
            <Grid item md={12} sm={12} xs={12} className="faq-mbl-items-grid">
              <Typography
                variant="h5"
                sx={{ textAlign: "center", width: "100%" }}
              >
                No categories found
              </Typography>
            </Grid>
          )
        ) : (
          <>
            {[1, 2, 3, 4].map((data) => {
              return (
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={6}
                  className="faq-mbl-items-grid"
                  key={data}
                >
                  <Typography className="faq-mbl-item" variant="subtitle2">
                    <CustomShimmer width="100%" height="1.063rem" />
                  </Typography>
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    );
  }, [showShimmer, faqCategories]);

  const handleChatOpen = useCallback(() => {
    openChatWithUs();
  }, []);

  return (
    <CustomPopup
      open={open}
      handleOpen={handleClosePopup}
      btnLabel={fromDetail ? "Close" : "Go back"}
      className={`faq-mbl-popup ${
        selectedCategory?.attributes.categoryName === "" ? "" : "faq-que-ans"
      } ${width > tabViewPoint ? "centered" : ""}`}
      btnStartIcon={
        fromDetail ? (
          <CloseIcon fontSize="small" />
        ) : (
          <KeyboardArrowLeftIcon fontSize="small" />
        )
      }
    >
      {selectedCategory?.attributes.categoryName === "" ? (
        <Box className="faq-mbl-popup-content" sx={{ textAlign: "center" }}>
          <Box>
            <Typography variant="h5" sx={{ color: Colors.textBlackV1 }}>
              FAQS
            </Typography>
            <Typography className="faq-category-title" variant="h5">
              Categories
            </Typography>
          </Box>
          {step === 1 ? renderFirstLevel() : renderSteps()}
          <Button className="faq-get-in-touch" onClick={handleChatOpen}>
            <Typography variant="h4">
              Didn’t find your question? Get in touch
            </Typography>
          </Button>
          {activeTab === "Loans" && (
            <Box className="faq-disclaimer">
              <Typography className="faq-disclaimer-title" variant="h6">
                {FAQDisclaimerTitle}
              </Typography>
              <Typography className="faq-disclaimer-content" variant="h6">
                {FAQDisclaimerContent}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <FaqItemList
          category={selectedCategory}
          handleCategory={handleCategory}
          selectedModule={selectedModule}
          handleChatOpen={openChatWithUs}
        />
      )}
    </CustomPopup>
  );
};
export default FAQsMobile;

export const RenderTitle: FC<ChildProps> = ({ data, handleCategory }) => {
  const { pathname } = useLocation();
  const handleCleverTapCTAClick = (data: T_FaqCategoryItems) => {
    TriggerGTAEvent("get_help_faq_cat_click", {
      screen_type: "hi_servicing",
      screen_name: "HelpPopUp",
      faq_category: data?.attributes.categoryName,
    });
    triggerCleverTapEvent("get_help_faq_cat_click", {
      ScreenName: "HelpPopUp",
      isPrevPageNameRequired: true,
      FaqCategory: data?.attributes.categoryName,
    });
  };
  const handleGTAEvents = (data: T_FaqCategoryItems) => {
    TriggerGTAEvent("get_help_faq_cat_click", {
      faq_category: data?.attributes.categoryName,
      screen_type: getGTAEventScreenType(pathname)?.screen_type,
      screen_name: getGTAEventScreenType(pathname)?.screen_name,
    });
  };
  const handleClick = useCallback(() => {
    handleCleverTapCTAClick(data);
    handleGTAEvents(data);
    handleCategory(data, true);
  }, [handleCategory, data]);
  return (
    <Typography
      className="faq-mbl-item"
      variant="subtitle2"
      onClick={handleClick}
    >
      {data.attributes.categoryName}
    </Typography>
  );
};
